import React, { useState } from 'react';
import { Search, Package, Truck, Home, CheckCircle, Clock, MapPin, Calendar } from 'lucide-react';
import { API_URL } from '../config/api';

const TrackOrderPage = () => {
  const [identifier, setIdentifier] = useState('');
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleTrack = async (e) => {
    e.preventDefault();
    
    if (!identifier.trim()) {
      setError('Please enter an order number or tracking number');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setTracking(null);

      const response = await fetch(`${API_URL}/api/tracking/${encodeURIComponent(identifier.trim())}`);
      const result = await response.json();

      if (result.success) {
        setTracking(result.tracking);
      } else {
        setError(result.error || 'Order not found');
      }
    } catch (err) {
      console.error('Error tracking order:', err);
      setError('Failed to track order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      paid: CheckCircle,
      processing: Package,
      shipped: Truck,
      delivered: Home
    };
    return icons[status] || Package;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      paid: 'bg-blue-100 text-blue-800 border-blue-300',
      processing: 'bg-purple-100 text-purple-800 border-purple-300',
      shipped: 'bg-indigo-100 text-indigo-800 border-indigo-300',
      delivered: 'bg-green-100 text-green-800 border-green-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getProgressPercentage = (status) => {
    const progress = {
      pending: 20,
      paid: 40,
      processing: 60,
      shipped: 80,
      delivered: 100
    };
    return progress[status] || 0;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <Package className="w-8 h-8 text-orange-600" />
          </div>
          <h1 className="text-3xl font-black text-gray-900 mb-2">Track Your Order</h1>
          <p className="text-gray-600">
            Enter your order number or tracking number to see where your order is
          </p>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <form onSubmit={handleTrack} className="flex gap-3">
            <div className="flex-1">
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Enter order number (e.g., ORD-20260828-1234) or tracking number"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Search className="w-5 h-5" />
              {loading ? 'Tracking...' : 'Track'}
            </button>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Tracking Results */}
        {tracking && (
          <div className="space-y-6">
            
            {/* Order Info Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-1">
                    Order #{tracking.order_number}
                  </h2>
                  <p className="text-sm text-gray-600">
                    Placed on {new Date(tracking.created_at).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
                <div className={`px-4 py-2 rounded-full text-sm font-bold border-2 ${getStatusColor(tracking.status)}`}>
                  {tracking.status.toUpperCase()}
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-orange-500 to-orange-600 transition-all duration-500"
                    style={{ width: `${getProgressPercentage(tracking.status)}%` }}
                  />
                </div>
              </div>

              {/* Shipping Info */}
              {tracking.courier_name && (
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-start gap-3">
                    <Truck className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Courier</p>
                      <p className="font-semibold text-gray-900">{tracking.courier_name}</p>
                    </div>
                  </div>
                  
                  {tracking.tracking_number && (
                    <div className="flex items-start gap-3">
                      <Package className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Tracking Number</p>
                        <p className="font-mono font-semibold text-gray-900 text-sm">
                          {tracking.tracking_number}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Delivery Info */}
              <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                {tracking.customer_addresses && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Delivering To</p>
                      <p className="font-semibold text-gray-900">
                        {tracking.customer_addresses.full_name}
                      </p>
                      <p className="text-sm text-gray-600">
                        {tracking.customer_addresses.city}, {tracking.customer_addresses.state} {tracking.customer_addresses.pincode}
                      </p>
                    </div>
                  </div>
                )}
                
                {tracking.estimated_delivery_date && (
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Estimated Delivery</p>
                      <p className="font-semibold text-gray-900">
                        {new Date(tracking.estimated_delivery_date).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Status Timeline */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6">Shipment Timeline</h3>
              
              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                
                {/* Timeline Events */}
                <div className="space-y-6">
                  {tracking.status_history && tracking.status_history.length > 0 ? (
                    tracking.status_history.map((event, idx) => {
                      const Icon = getStatusIcon(event.status);
                      const isLatest = idx === tracking.status_history.length - 1;
                      
                      return (
                        <div key={idx} className="relative flex items-start gap-4">
                          {/* Icon */}
                          <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center ${
                            isLatest ? 'bg-orange-600' : 'bg-green-600'
                          }`}>
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 pb-6">
                            <div className="flex items-start justify-between mb-1">
                              <h4 className="font-bold text-gray-900 capitalize">
                                {event.status.replace('_', ' ')}
                              </h4>
                              <span className="text-xs text-gray-600">
                                {new Date(event.created_at).toLocaleString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                            {event.notes && (
                              <p className="text-sm text-gray-600">{event.notes}</p>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No tracking updates yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Delivered Status */}
            {tracking.status === 'delivered' && tracking.delivered_at && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-green-900 mb-1">Order Delivered</h3>
                    <p className="text-sm text-green-800">
                      Your order was delivered on {new Date(tracking.delivered_at).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Help Section */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-bold text-gray-900 mb-3">Need Help?</h3>
              <p className="text-sm text-gray-600 mb-4">
                If you have any questions about your order or delivery, please contact our customer support.
              </p>
              <div className="flex gap-3">
                <a
                  href="/contact"
                  className="px-4 py-2 border-2 border-gray-300 text-gray-700 hover:bg-white font-semibold rounded-lg transition-colors text-sm"
                >
                  Contact Support
                </a>
                <a
                  href={`/orders/${tracking.order_number}`}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-lg transition-colors text-sm"
                >
                  View Order Details
                </a>
              </div>
            </div>

          </div>
        )}

        {/* Info Section (when no tracking) */}
        {!tracking && !error && !loading && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="font-bold text-blue-900 mb-3">How to Track Your Order</h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">1.</span>
                <span>Enter your order number (found in confirmation email) or tracking number</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">2.</span>
                <span>Click "Track" to see real-time updates</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">3.</span>
                <span>You can also track orders from "My Orders" section if you're logged in</span>
              </li>
            </ul>
          </div>
        )}

      </div>
    </div>
  );
};

export default TrackOrderPage;
